import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchLiveProductCatalog, fetchTldPrices } from '../api';

// Live view of the canonical code catalogs in squish-backend (lib/*-catalog.js)
// via GET /api/products/live. These are the objects checkout actually charges
// from — single source of truth per the price-catalog-only doctrine.
//
// Read-only ON PURPOSE. The old version of this page edited the
// `product-catalog` Salesforce config, which nothing else read (the endpoint
// serving it had zero consumers) — so edits went nowhere and the list sat
// frozen at 16 hand-typed products while the real catalogs grew. Price changes
// happen in the squish-backend repo, coupled with the reprice-*.js scripts
// that move live Stripe in the same PR.

const CHANNEL = {
  true:  { label: 'Self-Serve', color: '#4ade80' },
  false: { label: 'Sales-led',  color: '#f87171' },
};

const SECTIONS = [
  { key: 'hosting',       label: 'Hosting' },
  { key: 'hostingAddons', label: 'Hosting Add-ons' },
  { key: 'email',         label: 'Email' },
  { key: 'emailAddons',   label: 'Email Add-ons' },
  { key: 'domains',       label: 'Domains' },
];

export default function Products() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState(null);
  // Full TLD sheet loads separately — the upstream NameSilo call is slow, so
  // the SKU catalogs render immediately and this fills in when it lands.
  const [tldSheet, setTldSheet] = useState(null);
  const [tldError, setTldError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await fetchLiveProductCatalog());
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    fetchTldPrices().then(setTldSheet).catch(err => setTldError(err.message));
  }, []);

  if (loading) return <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>Loading...</div>;

  const hosting       = Object.values(data?.hosting || {});
  const hostingAddons = Object.values(data?.hostingAddons || {});
  const email         = Object.values(data?.email || {});
  const emailAddons   = Object.values(data?.emailAddons || {});
  const tlds          = data?.domains?.tlds || [];
  const hostingTerms  = data?.billingTerms?.hosting;
  const partialErrors = data?.errors || [];

  const skuCount = hosting.length + hostingAddons.length + email.length + emailAddons.length;
  const show = key => filter === null || filter === key;

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1100, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 4px', color: 'var(--text)' }}>Product Catalog</h1>
        <p style={{ fontSize: 13, color: 'var(--text3)', margin: 0 }}>
          Live from the canonical squish-backend catalogs — the exact objects checkout charges from.
          Read-only here: prices change in the squish-backend repo (coupled with the Stripe reprice scripts).
        </p>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', background: '#f871711a', border: '1px solid #f8717130', borderRadius: 8, color: '#f87171', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {partialErrors.length > 0 && (
        <div style={{ padding: '10px 14px', background: '#fbbf241a', border: '1px solid #fbbf2430', borderRadius: 8, color: '#fbbf24', fontSize: 13, marginBottom: 16 }}>
          Some catalogs failed to load: {partialErrors.map(e => e.section).join(', ')}
        </div>
      )}

      {/* Section filter */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 24, flexWrap: 'wrap' }}>
        <FilterBtn label="All" active={filter === null} onClick={() => setFilter(null)} />
        {SECTIONS.map(s => (
          <FilterBtn key={s.key} label={s.label} active={filter === s.key} onClick={() => setFilter(s.key)} />
        ))}
      </div>

      {show('hosting') && hosting.length > 0 && (
        <Section title="Hosting" count={hosting.length}>
          <CatalogTable
            head={['Product', 'SKU', 'Tier', 'Price', 'Cost', 'Margin', 'Channel']}
            rows={hosting.map(p => [
              <Name name={p.label} desc={p.desc} key="n" />,
              <Sku sku={p.sku} key="s" />,
              <span key="t" style={{ textTransform: 'capitalize' }}>{p.category}</span>,
              <Price key="p" display={p.priceDisplay} startingAt={p.pricingModel === 'starting-at'} />,
              <Money key="c" v={p.costMonthly} suffix="/mo" />,
              <Margin key="m" pct={p.marginPct} />,
              <Channel key="ch" selfServe={p.selfServe} />,
            ])}
          />
        </Section>
      )}

      {show('hostingAddons') && hostingAddons.length > 0 && (
        <Section title="Hosting Add-ons" count={hostingAddons.length}>
          <CatalogTable
            head={['Add-on', 'SKU', 'Family', 'Price', 'Cost', 'Margin', 'Channel']}
            rows={hostingAddons.map(p => [
              <Name name={p.label} desc={p.desc} key="n" />,
              <Sku sku={p.sku} key="s" />,
              <span key="f" style={{ textTransform: 'capitalize' }}>{p.family}</span>,
              <Price key="p" display={p.priceDisplay} />,
              <Money key="c" v={p.costMonthly ?? p.costOneTime} suffix={p.costMonthly != null ? '/mo' : ''} />,
              <Margin key="m" pct={p.marginPct} />,
              <Channel key="ch" selfServe={p.selfServe} />,
            ])}
          />
        </Section>
      )}

      {show('email') && email.length > 0 && (
        <Section title="Email (M365 via Pax8)" count={email.length}>
          <CatalogTable
            head={['Plan', 'SKU', 'Microsoft product', 'Monthly', 'Annual', 'MS direct (annual)', 'Channel']}
            rows={email.map(p => [
              <Name name={p.label} key="n" />,
              <Sku sku={p.sku} key="s" />,
              <span key="ms" style={{ color: 'var(--text3)' }}>{p.msProductName}</span>,
              <Money key="m" v={p.monthly} suffix="/user/mo" bold />,
              <Money key="a" v={p.annual} suffix="/user/mo" />,
              <Money key="d" v={p.msDirectAnnual} suffix="/user/mo" muted />,
              <Channel key="ch" selfServe={p.selfServe} />,
            ])}
          />
        </Section>
      )}

      {show('emailAddons') && emailAddons.length > 0 && (
        <Section title="Email Add-ons" count={emailAddons.length}>
          <CatalogTable
            head={['Add-on', 'SKU', 'Microsoft product', 'Monthly', 'Annual', 'Requires', 'Channel']}
            rows={emailAddons.map(p => [
              <Name name={p.label} desc={p.desc} key="n" />,
              <Sku sku={p.sku} key="s" />,
              <span key="ms" style={{ color: 'var(--text3)' }}>{p.msProductName}</span>,
              <Money key="m" v={p.annualOnly ? null : p.monthly} suffix="/user/mo" bold />,
              <Money key="a" v={p.annual} suffix="/user/mo" />,
              <Sku sku={p.requiresBaseTier} key="r" />,
              <Channel key="ch" selfServe={p.selfServe} />,
            ])}
          />
        </Section>
      )}

      {show('domains') && tlds.length > 0 && (
        <Section title="Domains — featured (marketing floors)" count={tlds.length}>
          <CatalogTable
            head={['TLD', 'Badge', 'First year', 'Renewal', 'Notes']}
            rows={tlds.map(t => [
              <span key="t" style={{ fontWeight: 600, fontFamily: 'monospace', color: 'var(--text)' }}>{t.tld}</span>,
              t.badge ? <Badge key="b" text={t.badge} color="#818cf8" /> : null,
              t.livePrice
                ? <span key="f" style={{ color: '#fbbf24', fontWeight: 600 }}>Live pricing</span>
                : <Money key="f" v={t.floorPrice} suffix="/yr" bold />,
              t.livePrice ? <span key="r" style={{ color: 'var(--text3)' }}>—</span> : <Money key="r" v={t.renewalPrice} suffix="/yr" />,
              <span key="n" style={{ color: 'var(--text3)', fontSize: 12 }}>
                {t.renewalPrice > t.floorPrice ? 'Promo first year — renews higher' : t.blurb}
              </span>,
            ])}
          />
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 8 }}>
            The 8 headlined TLDs from lib/domain-catalog.js (storefront grid). Every TLD we sell is below.
          </div>
        </Section>
      )}

      {show('domains') && <TldPriceSheet sheet={tldSheet} error={tldError} />}

      <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 20, lineHeight: 1.7 }}>
        {skuCount} SKUs + {tlds.length} TLDs, live from squish-backend /api/v2/internal/catalogs/* (full projections — the public /api/catalog/* strips cost + margin).
        {hostingTerms && (
          <> Hosting terms: {hostingTerms.termsMonths.map(m =>
            `${hostingTerms.label[m]} ×${hostingTerms.rateFactor[m].toFixed(2)}`).join(' · ')} (billing-terms.js).</>
        )}
        {' '}Cart builder line items still come from Salesforce Product2 (separate list).
      </div>
    </div>
  );
}

// Full NameSilo TLD sheet — hundreds of rows, so it gets a search box and a
// scroll container instead of a plain table. Retail = cost +20% rounded up
// (whole dollars); transfers at cost (loss leader).
function TldPriceSheet({ sheet, error }) {
  const [q, setQ] = useState('');
  const rows = useMemo(() => {
    const prices = sheet?.prices || {};
    const needle = q.trim().toLowerCase().replace(/^\./, '');
    return Object.entries(prices)
      .filter(([tld]) => !needle || tld.includes(needle))
      .sort(([a], [b]) => a.localeCompare(b));
  }, [sheet, q]);
  const total = Object.keys(sheet?.prices || {}).length;

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
          Domains — all TLDs{total > 0 && <span style={{ color: 'var(--text3)', fontWeight: 400 }}> ({total})</span>}
        </h2>
        {sheet?.stale && <Badge text="Stale cache" color="#fbbf24" />}
        {total > 0 && (
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Filter TLDs…"
            style={{
              marginLeft: 'auto', padding: '6px 10px', fontSize: 12, width: 180,
              background: 'var(--bg2)', color: 'var(--text)',
              border: '1px solid var(--border)', borderRadius: 6,
            }}
          />
        )}
      </div>

      {error && (
        <div style={{ padding: '10px 14px', background: '#f871711a', border: '1px solid #f8717130', borderRadius: 8, color: '#f87171', fontSize: 13 }}>
          TLD price sheet failed to load: {error}
        </div>
      )}
      {!error && !sheet && (
        <div style={{ padding: '14px', color: 'var(--text3)', fontSize: 13 }}>
          Loading full TLD price sheet from NameSilo… (can take a few seconds)
        </div>
      )}

      {total > 0 && (
        <>
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, overflowX: 'auto' }}>
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--bg2)', zIndex: 1 }}>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <Th>TLD</Th><Th>Registration</Th><Th>Renewal</Th><Th>Transfer</Th><Th>Our cost (reg)</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(([tld, p]) => (
                    <tr key={tld} style={{ borderBottom: '1px solid var(--border)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '8px 14px', fontFamily: 'monospace', fontWeight: 600, color: 'var(--text)' }}>.{tld}</td>
                      <td style={{ padding: '8px 14px' }}><Money v={p.registration} suffix="/yr" bold /></td>
                      <td style={{ padding: '8px 14px' }}><Money v={p.renewal} suffix="/yr" /></td>
                      <td style={{ padding: '8px 14px' }}><Money v={p.transfer} muted /></td>
                      <td style={{ padding: '8px 14px' }}><Money v={p.cost?.registration} muted /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length === 0 && (
                <div style={{ padding: 16, color: 'var(--text3)', fontSize: 13 }}>No TLD matches "{q}".</div>
              )}
            </div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 8 }}>
            Live NameSilo sheet (cached 6h). Retail = cost +20% rounded to whole dollars; transfers at cost.
          </div>
        </>
      )}
    </div>
  );
}

function Section({ title, count, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: '0 0 10px' }}>
        {title} <span style={{ color: 'var(--text3)', fontWeight: 400 }}>({count})</span>
      </h2>
      {children}
    </div>
  );
}

function CatalogTable({ head, rows }) {
  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            {head.map(h => <Th key={h}>{h}</Th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells, i) => (
            <tr key={i} style={{ borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              {cells.map((c, j) => <td key={j} style={{ padding: '10px 14px' }}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FilterBtn({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 14px', fontSize: 12, fontWeight: active ? 600 : 400,
      background: active ? 'var(--accent)' : 'var(--bg2)', color: active ? '#fff' : 'var(--text3)',
      border: active ? 'none' : '1px solid var(--border)', borderRadius: 6, cursor: 'pointer',
    }}>{label}</button>
  );
}

function Th({ children }) {
  return (
    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--text3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {children}
    </th>
  );
}

function Name({ name, desc }) {
  return (
    <div>
      <div style={{ fontWeight: 500, color: 'var(--text)' }}>{name}</div>
      {desc && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{desc}</div>}
    </div>
  );
}

function Sku({ sku }) {
  if (!sku) return <span style={{ color: 'var(--text3)' }}>—</span>;
  return <span style={{ color: 'var(--text3)', fontFamily: 'monospace', fontSize: 11 }}>{sku}</span>;
}

function Price({ display, startingAt }) {
  return (
    <span style={{ fontWeight: 600, color: 'var(--text)' }}>
      {startingAt && <span style={{ fontWeight: 400, color: 'var(--text3)' }}>from </span>}
      {display}
    </span>
  );
}

function Money({ v, suffix = '', bold, muted }) {
  if (v == null) return <span style={{ color: 'var(--text3)' }}>—</span>;
  return (
    <span style={{ fontWeight: bold ? 600 : 400, color: muted ? 'var(--text3)' : 'var(--text)' }}>
      ${Number(v).toLocaleString()}<span style={{ color: 'var(--text3)', fontWeight: 400 }}>{suffix}</span>
    </span>
  );
}

function Margin({ pct }) {
  if (pct == null) return <span style={{ color: 'var(--text3)' }}>—</span>;
  const color = pct >= 40 ? '#4ade80' : pct >= 25 ? '#fbbf24' : '#f87171';
  return <span style={{ fontWeight: 600, color }}>{pct}%</span>;
}

function Channel({ selfServe }) {
  const { label, color } = CHANNEL[!!selfServe];
  return <Badge text={label} color={color} />;
}

function Badge({ text, color }) {
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 4,
      background: `${color}20`, color, whiteSpace: 'nowrap',
    }}>{text}</span>
  );
}
